import React, { useEffect, useRef, useState } from 'react';
import { Layers, Code, Link2, Cloud, Settings, GraduationCap } from 'lucide-react';
import { ImageWithFallback } from '../figma/ImageWithFallback.tsx';
import './ServiceCards.css';

const services = [
  {
    icon: Layers,
    title: 'Platform Solutions',
    shortDesc: 'Enterprise-grade platforms tailored to your business needs.',
    imageUrl: 'https://images.unsplash.com/photo-1759752394755-1241472b589d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxlbnRlcnByaXNlJTIwZGFzaGJvYXJkJTIwcGxhdGZvcm18ZW58MXx8fHwxNzY0MzYyMjY4fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
    description: 'We build robust, scalable platforms that grow with your business. Our solutions integrate seamlessly with your existing systems while providing the flexibility to adapt to changing market demands.',
    features: ['Scalable Architecture', 'Real-time Analytics', 'Custom APIs', 'Multi-tenant Support'],
    benefits: 'Reduce operational costs by 40% while increasing system performance and reliability.',
  },
  {
    icon: Code,
    title: 'Custom Development',
    shortDesc: 'Bespoke software solutions built from the ground up.',
    imageUrl: 'https://images.unsplash.com/photo-1531498860502-7c67cf02f657?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxzb2Z0d2FyZSUyMGRldmVsb3BtZW50JTIwY29kaW5nfGVufDF8fHx8MTc2NDI2OTc0OXww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
    description: 'Transform your unique ideas into powerful digital solutions. Our expert developers use cutting-edge technologies and agile methodologies to deliver software that perfectly matches your requirements.',
    features: ['Web & Mobile Apps', 'E-commerce Platforms', 'SaaS Products', 'Legacy Modernization'],
    benefits: 'Accelerate time-to-market with solutions designed specifically for your business needs.',
  },
  {
    icon: Link2,
    title: 'System Integrations',
    shortDesc: 'Seamlessly connect your tools and workflows.',
    imageUrl: 'https://images.unsplash.com/photo-1738082956220-a1f20a8632ce?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxhcGklMjBpbnRlZ3JhdGlvbiUyMG5ldHdvcmt8ZW58MXx8fHwxNzY0MjQxNDY4fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
    description: 'Break down data silos and create unified workflows. We connect disparate systems, automate processes, and ensure your applications communicate flawlessly to maximize efficiency.',
    features: ['Third-party APIs', 'Workflow Automation', 'Data Synchronization', 'Middleware Solutions'],
    benefits: 'Eliminate manual data entry and reduce errors while improving team productivity by 60%.',
  },
  {
    icon: Cloud,
    title: 'Cloud & Data Solutions',
    shortDesc: 'Modern cloud infrastructure and data engineering.',
    imageUrl: 'https://images.unsplash.com/photo-1759752394755-1241472b589d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjbG91ZCUyMGNvbXB1dGluZyUyMGRhdGF8ZW58MXx8fHwxNzY0MzEwODczfDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
    description: 'Leverage the power of cloud computing with our comprehensive infrastructure and data solutions. From migration to optimization, we ensure your data is secure, accessible, and actionable.',
    features: ['Cloud Migration', 'Data Pipelines', 'Infrastructure as Code', 'DevOps Automation'],
    benefits: 'Cut infrastructure costs by up to 50% while improving scalability and disaster recovery.',
  },
  {
    icon: Settings,
    title: 'Managed Services',
    shortDesc: 'Ongoing support and optimization for your systems.',
    imageUrl: 'https://images.unsplash.com/photo-1761141535640-c78744c4f369?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtb25pdG9yaW5nJTIwc3lzdGVtJTIwZGFzaGJvYXJkfGVufDF8fHx8MTc2NDM2MjI2OXww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
    description: 'Focus on your core business while we handle the technical complexities. Our managed services provide proactive monitoring, maintenance, and optimization to keep your systems running at peak performance.',
    features: ['24/7 Monitoring', 'Performance Tuning', 'Security Updates', 'Incident Response'],
    benefits: 'Achieve 99.9% uptime with proactive monitoring and rapid issue resolution.',
  },
  {
    icon: GraduationCap,
    title: 'Training & Enablement',
    shortDesc: 'Upskill your team with expert-led programs.',
    imageUrl: 'https://images.unsplash.com/photo-1761250246894-ee2314939662?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjB0cmFpbmluZyUyMHdvcmtzaG9wfGVufDF8fHx8MTc2NDI5MTAyM3ww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
    description: 'Empower your workforce with comprehensive training programs designed to build expertise and drive adoption. Our hands-on approach ensures your team masters the tools and technologies they need to succeed.',
    features: ['Custom Workshops', 'Certification Programs', 'Hands-on Projects', 'Ongoing Mentorship'],
    benefits: 'Boost team productivity by 45% through targeted skill development and practical training.',
  },
];

export function ServiceCards() {
  const [visibleCards, setVisibleCards] = useState<number[]>([]);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = cardsRef.current.indexOf(entry.target as HTMLDivElement);
            if (index !== -1 && !visibleCards.includes(index)) {
              setVisibleCards((prev) => [...prev, index]);
            }
          }
        });
      },
      { threshold: 0.1 }
    );

    cardsRef.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => {
      cardsRef.current.forEach((card) => {
        if (card) observer.unobserve(card);
      });
    };
  }, [visibleCards]);

  return (
    <section className="service-cards section">
      <div className="service-cards-header">
        <h2 className="section-title">Comprehensive Technology Solutions</h2>
        <p className="section-subtitle">
          End-to-end services designed to accelerate your digital transformation journey and drive measurable business results.
        </p>
      </div>

      <div className="service-grid">
        {services.map((service, index) => {
          const Icon = service.icon;
          return (
            <div
              key={index}
              ref={(el) => (cardsRef.current[index] = el)}
              className={`service-card ${visibleCards.includes(index) ? 'visible' : ''}`}
              style={{ animationDelay: `${(index % 3) * 0.1}s` }}
            >
              <div className="service-card-header">
                <div className="service-icon-badge">
                  <Icon size={28} />
                </div>
                <div className="service-header-text">
                  <h3 className="service-title">{service.title}</h3>
                  <p className="service-short-desc">{service.shortDesc}</p>
                </div>
              </div>

              <div className="service-image-wrapper">
                <ImageWithFallback
                  src={service.imageUrl}
                  alt={service.title}
                  className="service-image"
                />
                <div className="image-overlay"></div>
              </div>

              <div className="service-content">
                <p className="service-description">{service.description}</p>

                <div className="service-features-section">
                  <h4 className="features-heading">Key Capabilities:</h4>
                  <ul className="service-features">
                    {service.features.map((feature, idx) => (
                      <li key={idx}>{feature}</li>
                    ))}
                  </ul>
                </div>

                <div className="service-benefits">
                  <div className="benefits-icon">💡</div>
                  <p className="benefits-text">{service.benefits}</p>
                </div>

                <button className="service-btn">
                  Learn More
                  <span className="btn-arrow">→</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
